import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {TemplateResponseBody} from '@dms/api/core';

import {Nullish} from 'shared/web';

/**
 * Keeps a variant's stored templateId when it still belongs to the given document definition,
 * otherwise repoints it to that definition's primary template. Used when a document code is
 * replaced and previously saved ids point to the retired definition.
 *
 * Never clears a stored id: without a primary template to repoint to, the id is left untouched.
 * Clearing would blank a required field and block saving the whole variant form, and deposit
 * templateId has no required check at all, so it would be dropped silently.
 */
export const resolveVariantTemplateId = (
  templates: Pick<TemplateResponseBody, 'id'>[] | Nullish,
  primaryTemplateId: string | Nullish,
  currentTemplateId: string | Nullish
): string | undefined =>
  match({
    hasCurrentTemplateId: isNotNil(currentTemplateId),
    hasTemplates: isNotNilOrEmpty(templates),
    isCurrentTemplateAvailable: Boolean(
      templates?.some((template) => template.id === currentTemplateId)
    ),
  })
    .with({hasCurrentTemplateId: false}, () => undefined)
    .with({hasTemplates: false}, () => currentTemplateId ?? undefined)
    .with({isCurrentTemplateAvailable: true}, () => currentTemplateId ?? undefined)
    .otherwise(() => primaryTemplateId ?? currentTemplateId ?? undefined);
